import { getPlatformLogo } from '@/helpers/environmentHelper';
import usePDFTheme from '@/utils/hooks/usePDFTheme';
import { Defs, Image, LinearGradient, Rect, Stop, StyleSheet, Svg, Text, View } from '@react-pdf/renderer';
import { useMemo } from 'react';
import { PortfolioStatementProps } from './PortfolioStatement';

/**
 * The gradient is painted by an SVG layer behind the content. @react-pdf's
 * Svg needs concrete dimensions, so the card is a fixed box: A4 width (595.28)
 * minus the page's 36pt gutters, at a height that fits the meta + balance rows.
 */
const CARD_WIDTH = 523;
const CARD_HEIGHT = 186;

const PortfolioStatementHeader = ({ metadata }: PortfolioStatementProps) => {
    const { date, entity, account, currency, balance } = metadata;

    const pdfTheme = usePDFTheme();
    const { palette, fontFamily } = pdfTheme;

    const styles = useMemo(() => {
        return StyleSheet.create({
            logo: {
                flexDirection: 'row',
                alignItems: 'center'
            },
            card: {
                position: 'relative',
                width: CARD_WIDTH,
                height: CARD_HEIGHT,
                borderRadius: 12,
                overflow: 'hidden',
                backgroundColor: palette.primaryInk
            },
            backdrop: {
                position: 'absolute',
                top: 0,
                left: 0
            },
            content: {
                paddingVertical: 24,
                paddingHorizontal: 26
            },
            topRow: {
                flexDirection: 'row',
                justifyContent: 'space-between',
                alignItems: 'flex-start'
            },
            title: {
                fontSize: 21,
                color: palette.onPrimary,
                fontFamily,
                fontWeight: 600
            },
            titleRule: {
                width: 34,
                height: 3,
                borderRadius: 2,
                backgroundColor: palette.primary,
                marginTop: 9
            },
            body: {
                flexDirection: 'row',
                justifyContent: 'space-between',
                alignItems: 'flex-end',
                marginTop: 22
            },
            metaBlock: {
                width: '52%'
            },
            metaRow: {
                flexDirection: 'row',
                alignItems: 'center',
                marginBottom: 7
            },
            metaLabel: {
                width: 68,
                fontSize: 6.8,
                letterSpacing: 1.3,
                color: palette.primarySoft,
                fontFamily,
                fontWeight: 400
            },
            metaValue: {
                fontSize: 9.5,
                color: palette.onPrimary,
                fontFamily,
                fontWeight: 500
            },
            balanceBlock: {
                alignItems: 'flex-end'
            },
            balanceLabel: {
                fontSize: 7,
                letterSpacing: 1.6,
                color: palette.primaryMid,
                fontFamily,
                fontWeight: 600
            },
            balance: {
                fontSize: 26,
                marginTop: 7,
                color: palette.onPrimary,
                fontFamily,
                fontWeight: 700
            },
            balanceRule: {
                height: 2,
                width: 96,
                marginTop: 10,
                borderRadius: 1,
                backgroundColor: palette.primary
            }
        });
    }, [fontFamily, palette]);

    const meta = [
        { label: 'DATE', value: date },
        { label: 'ENTITY', value: entity },
        { label: 'ACCOUNT', value: account },
        { label: 'CURRENCY', value: currency }
    ];

    return (
        <View style={styles.card}>
            <Svg
                style={styles.backdrop}
                width={CARD_WIDTH}
                height={CARD_HEIGHT}
                viewBox={`0 0 ${CARD_WIDTH} ${CARD_HEIGHT}`}>
                <Defs>
                    <LinearGradient
                        id="PortfolioStatementHeaderGradient"
                        x1="0"
                        y1={CARD_HEIGHT}
                        x2={CARD_WIDTH}
                        y2="0"
                        gradientUnits="userSpaceOnUse">
                        <Stop offset="0" stopColor={palette.primaryInk} />
                        <Stop offset="0.55" stopColor={palette.primaryInk} />
                        <Stop offset="1" stopColor={palette.primaryDeep} />
                    </LinearGradient>
                </Defs>
                <Rect
                    x="0"
                    y="0"
                    width={CARD_WIDTH}
                    height={CARD_HEIGHT}
                    fill="url(#PortfolioStatementHeaderGradient)"
                />
            </Svg>

            <View style={styles.content}>
                <View style={styles.topRow}>
                    <View>
                        <Text style={styles.title}>Client Statement</Text>
                        <View style={styles.titleRule} />
                    </View>
                    <View style={styles.logo}>
                        <Image src={getPlatformLogo()} style={{ height: 40 }} />
                    </View>
                </View>

                <View style={styles.body}>
                    <View style={styles.metaBlock}>
                        {meta.map((item) => (
                            <View key={item.label} style={styles.metaRow}>
                                <Text style={styles.metaLabel}>{item.label}</Text>
                                <Text style={styles.metaValue}>{item.value}</Text>
                            </View>
                        ))}
                    </View>

                    <View style={styles.balanceBlock}>
                        <Text style={styles.balanceLabel}>CURRENT BALANCE</Text>
                        <Text style={styles.balance}>{balance}</Text>
                        <View style={styles.balanceRule} />
                    </View>
                </View>
            </View>
        </View>
    );
};

export default PortfolioStatementHeader;
